/**
 * OneSignal Push通知 初期化ライブラリ
 *
 * Phase 0: 技術的実現可能性検証
 * - Web Push通知の購読
 * - External User ID によるユーザー識別
 *
 * @version 1.0.0
 * @date 2025-12-12
 * @issue #482
 */

import OneSignal from 'react-onesignal';

/** OneSignal App ID（環境変数から取得、改行文字を除去） */
const ONESIGNAL_APP_ID = import.meta.env.VITE_ONESIGNAL_APP_ID?.trim();

/** 初期化済みフラグ */
let isInitialized = false;

/**
 * OneSignal初期化オプション
 */
export interface OneSignalInitOptions {
  /** サービスワーカーのパス（デフォルト: /OneSignalSDKWorker.js） */
  serviceWorkerPath?: string;
  /** localhostを許可（開発環境用） */
  allowLocalhostAsSecureOrigin?: boolean;
}

/**
 * OneSignalを初期化
 *
 * 認証完了後に呼び出すことを推奨
 *
 * @returns 初期化成功時true
 *
 * @example
 * ```typescript
 * // 認証後に呼び出し
 * useEffect(() => {
 *   if (isAuthenticated) {
 *     initOneSignal();
 *   }
 * }, [isAuthenticated]);
 * ```
 */
export async function initOneSignal(
  options: OneSignalInitOptions = {}
): Promise<boolean> {
  // 既に初期化済みの場合はスキップ
  if (isInitialized) {
    console.log('[OneSignal] Already initialized');
    return true;
  }

  // App IDが設定されていない場合は警告
  if (!ONESIGNAL_APP_ID) {
    console.warn('[OneSignal] VITE_ONESIGNAL_APP_ID is not configured');
    return false;
  }

  try {
    await OneSignal.init({
      appId: ONESIGNAL_APP_ID,
      allowLocalhostAsSecureOrigin:
        options.allowLocalhostAsSecureOrigin ?? import.meta.env.DEV, // 開発環境ではデフォルトtrue
      serviceWorkerPath: options.serviceWorkerPath,
    });

    isInitialized = true;
    console.log('[OneSignal] Initialized successfully');
    return true;
  } catch (error) {
    console.error('[OneSignal] Initialization failed:', error);
    return false;
  }
}

/**
 * ユーザーIDをOneSignalに設定（External User ID）
 *
 * ログイン後に呼び出し、バックエンドからの通知送信時に使用される
 *
 * @param userId - RipiaユーザーID
 *
 * @example
 * ```typescript
 * // ログイン成功後
 * await setOneSignalUser(user.id);
 * ```
 */
export async function setOneSignalUser(userId: string): Promise<void> {
  if (!isInitialized) {
    console.warn('[OneSignal] Not initialized, cannot set user');
    return;
  }

  if (!userId) {
    console.warn('[OneSignal] Invalid userId provided');
    return;
  }

  try {
    // react-onesignal v2 API: login() でExternal User IDを設定
    await OneSignal.login(userId);
    console.log(`[OneSignal] User ID set: ${userId}`);
  } catch (error) {
    console.error('[OneSignal] Failed to set user ID:', error);
  }
}

/**
 * ユーザーIDをクリア（ログアウト時）
 *
 * @example
 * ```typescript
 * // ログアウト時
 * await clearOneSignalUser();
 * ```
 */
export async function clearOneSignalUser(): Promise<void> {
  if (!isInitialized) {
    return;
  }

  try {
    await OneSignal.logout();
    console.log('[OneSignal] User logged out');
  } catch (error) {
    console.error('[OneSignal] Failed to logout:', error);
  }
}

/**
 * Push通知許可プロンプトを表示
 *
 * ユーザーにPush通知の許可を求めるスライドダウンを表示
 * OneSignal未初期化時はネイティブNotification APIにフォールバック
 *
 * @returns 許可リクエストが成功した場合true
 *
 * @example
 * ```typescript
 * // ボタンクリックで呼び出し
 * <Button onClick={showPushPrompt}>通知を許可</Button>
 * ```
 */
export async function showPushPrompt(): Promise<boolean> {
  // OneSignal未初期化時はネイティブAPIへフォールバック
  if (!isInitialized) {
    console.warn('[OneSignal] Not initialized, using native Notification API');
    if ('Notification' in window) {
      try {
        const permission = await Notification.requestPermission();
        console.log('[Notification] Permission result:', permission);
        return permission === 'granted';
      } catch (error) {
        console.error('[Notification] Permission request failed:', error);
        return false;
      }
    }
    return false;
  }

  try {
    await OneSignal.Slidedown.promptPush();
    console.log('[OneSignal] Push prompt shown');
    return true;
  } catch (error) {
    console.error('[OneSignal] Failed to show push prompt:', error);
    return false;
  }
}

/**
 * Push通知の許可状態を取得
 *
 * @returns 許可状態（'default' | 'granted' | 'denied'）
 */
export async function getPushPermissionState(): Promise<NotificationPermission> {
  if (!isInitialized) {
    return 'default';
  }

  try {
    // ブラウザのPermission APIを使用
    const permission = await Notification.requestPermission();
    return permission;
  } catch (error) {
    console.error('[OneSignal] Failed to get permission state:', error);
    return 'default';
  }
}

/**
 * OneSignalが初期化済みかどうかを確認
 */
export function isOneSignalInitialized(): boolean {
  return isInitialized;
}

/**
 * OneSignalが設定されているかどうかを確認
 */
export function isOneSignalConfigured(): boolean {
  return !!ONESIGNAL_APP_ID;
}
